/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import Dashboard, { DashboardSection, DashboardSectionTitle } from '../../../../components/dashboard';
import { useAppSelector } from '../../../../reducers';
import { isStaff } from '../../../../selectors/user';
import IconButton from '../../../../components/button/IconButton';
import LoadingPlaceholder from '../../../../components/LoaderContainer/LoadingPlaceholder';
import { BlockingLoader } from '../../../../components/loader/BlockingLoader';
import { useDeleteSurveyMutation, useGetSurveysQuery } from '../../api/materiality-assessment';
import { useCreateSurveyMutation } from '../../api/materiality-assessment';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { formatDate } from '../../../../utils/date';
import { DataPeriods } from '../../../../types/universalTracker';
import { Blueprints } from '../../../../components/survey-configuration/types';
import { useNavigate } from 'react-router-dom-v5-compat';
import { getV6Router } from '../../../common/utils/V6Router';
import { V6Route } from '../../../common/types/V6Router';
import Table from '../../../../components/table/Table';
import { SurveyModelMinData } from '../../../../model/surveyData';
import { useSiteAlert } from '../../../../hooks/useSiteAlert';
import { SiteAlertColors } from '../../../../slice/siteAlertsSlice';
import { ExtraFeature, FeatureStability } from '@g17eco/molecules';

interface AssessmentsListProps {
  initiativeId: string;
};

const router = getV6Router();

export const AssessmentsList = (props: AssessmentsListProps) => {
  const { initiativeId } = props;
  const { data: surveys, isFetching: isGettingSurveys } = useGetSurveysQuery({ initiativeId });
  const [deleteSurvey, { isLoading: isDeleting }] = useDeleteSurveyMutation();
  const [createSurvey, { isLoading: isCreating }] = useCreateSurveyMutation();
  const { addSiteAlert } = useSiteAlert();

  const userIsStaff = useAppSelector(isStaff);
  const navigate = useNavigate();
  const handleGoToTestingTool = () => navigate(
    router.generateUrl(V6Route.MaterialityAssessmentTestingTool, { initiativeId })
  );

  const handleCreate = () => {
    createSurvey({
      name: 'Materiality Assessment',
      initiativeId,
      sourceName: Blueprints.Materiality2024,
      period: DataPeriods.Yearly,
      effectiveDate: new Date().toISOString(),
      evidenceRequired: false,
      noteRequired: false,
      verificationRequired: false,
      isPrivate: true,
    })
      .catch(() => {
        addSiteAlert({
          color: SiteAlertColors.Danger,
          content: 'Failed to create survey',
        })
      });
  }

  const handleDelete = (surveyId: string) => {
    deleteSurvey({ surveyId })
      .then(() => {
        const url = router.generateUrl(V6Route.MaterialityAssessmentSurveysList, { initiativeId });
        navigate(url);
      })
      .catch(() => {
        addSiteAlert({
          color: SiteAlertColors.Danger,
          content: 'Failed to delete survey',
        })
      });
  }

  const handleGoToSurvey = (surveyId: string) => {
    const url = router.generateUrl(V6Route.MaterialityAssessmentSurvey, { initiativeId, surveyId });
    navigate(url);
  }

  const isLoading = isCreating || isDeleting || isGettingSurveys;
  return (
    <Dashboard>
      {isLoading ? <BlockingLoader /> : null}
      <LoadingPlaceholder isLoading={isGettingSurveys}>
        <DashboardSectionTitle title={<span>Assessments <FeatureStability feature={ExtraFeature.MaterialityAssessmentTool} /></span>}
          buttons={
            [
              <Button onClick={handleCreate} className='me-2'><i className='fal fa-plus me-2' />Create</Button>,
              userIsStaff ? <IconButton color='primary' outline icon='fal fa-cog' onClick={handleGoToTestingTool} /> : null
            ]
          } />
        <DashboardSection>
          {surveys ? <Table<SurveyModelMinData>
            data={surveys}
            columns={[
              {
                Header: 'Name',
                accessor: ({ _id, name }) => <Button color='link' onClick={() => handleGoToSurvey(_id)}>{name}</Button>
              },
              {
                Header: 'Date',
                accessor: ({ effectiveDate }) => formatDate(effectiveDate)
              },
              {
                Header: ' ',
                cellProps: {
                  className: 'text-center'
                },
                accessor: ({ _id }) => (
                  <UncontrolledDropdown style={{
                    position: 'inherit'
                  }}>
                    <DropdownToggle color='transparent' outline className='px-2'>
                      <i className='fal fa-bars' />
                    </DropdownToggle>
                    <DropdownMenu>
                      <DropdownItem onClick={() => handleDelete(_id)}><i className='fa fa fa-trash me-2' color='danger' />Delete</DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                )
              }
            ]}
          />
            :
            <div className='text-center'>
              <Button onClick={handleCreate} color='link'>Create a new assessment</Button>
            </div>
          }
        </DashboardSection>
      </LoadingPlaceholder>
    </Dashboard>
  );
}
